import React, { useEffect, useState } from 'react';
import {
  getVisiblePageNumbers,
  getPageRangeLabel,
  getCursorPageRangeLabel,
  getCursorSoftTotalPages,
  PAGE_SIZE_OPTIONS,
} from '../../utils/listPagination.js';

/**
 * リスト用ページネーション（compact: 右上小型 / default: 下部中央大型）
 * mode="cursor": 総件数不明のカーソルページング（ページ番号・ジャンプ対応）
 */
const ListPagination = ({
  variant = 'default',
  mode = 'offset',
  currentPage,
  totalPages,
  total,
  pageSize,
  onPageChange,
  onPageSizeChange,
  minTotalToShow = PAGE_SIZE_OPTIONS[0],
  disabled = false,
  className = '',
  // cursor mode
  maxKnownPage,
  hasMore = false,
  itemCount = 0,
  allowJump = false,
}) => {
  const isCursor = mode === 'cursor';
  const softTotalPages = isCursor
    ? getCursorSoftTotalPages(maxKnownPage ?? currentPage, hasMore)
    : totalPages;
  const rangeLabel = isCursor
    ? getCursorPageRangeLabel(currentPage, pageSize, itemCount, {
      hasMore,
      maxKnownPage: maxKnownPage ?? currentPage,
    })
    : getPageRangeLabel(currentPage, pageSize, total);

  const [jumpValue, setJumpValue] = useState(String(currentPage || 1));
  useEffect(() => {
    setJumpValue(String(currentPage || 1));
  }, [currentPage]);

  if (!isCursor && total < minTotalToShow) return null;
  if (isCursor && softTotalPages <= 1 && !onPageSizeChange && itemCount === 0) return null;

  const pages = getVisiblePageNumbers(currentPage, Math.max(1, softTotalPages || 1));
  const isCompact = variant === 'compact';

  const goToPage = (page) => {
    if (disabled || page === currentPage || page < 1) return;
    if (!isCursor && page > totalPages) return;
    onPageChange(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submitJump = (event) => {
    event?.preventDefault?.();
    const parsed = Number.parseInt(String(jumpValue).trim(), 10);
    if (!Number.isFinite(parsed) || parsed < 1) {
      setJumpValue(String(currentPage || 1));
      return;
    }
    const target = isCursor ? parsed : Math.min(parsed, softTotalPages);
    setJumpValue(String(target));
    goToPage(target);
  };

  const prevDisabled = disabled || currentPage <= 1;
  const nextDisabled = disabled || currentPage >= softTotalPages;

  const pageButtons = pages.map((page, idx) =>
    page === '...' ? (
      <span key={`ellipsis-${idx}`} className="list-pagination__ellipsis" aria-hidden="true">…</span>
    ) : (
      <button
        key={`page-${page}`}
        type="button"
        className={`list-pagination__page${page === currentPage ? ' is-active' : ''}`}
        disabled={disabled || page === currentPage}
        onClick={() => goToPage(page)}
        aria-label={`${page}ページ目`}
        aria-current={page === currentPage ? 'page' : undefined}
      >
        {page}
      </button>
    )
  );

  const jumpControl = allowJump ? (
    <form className="list-pagination__jump" onSubmit={submitJump}>
      <label className="list-pagination__jump-label" htmlFor={`list-pagination-jump-${variant}`}>
        ページ
      </label>
      <input
        id={`list-pagination-jump-${variant}`}
        className="list-pagination__jump-input"
        type="number"
        min={1}
        max={isCursor ? undefined : softTotalPages}
        value={jumpValue}
        disabled={disabled}
        onChange={(e) => setJumpValue(e.target.value)}
        aria-label="ページ番号を入力"
      />
      {!isCursor && (
        <span className="list-pagination__jump-suffix">/ {softTotalPages}</span>
      )}
      {isCursor && (
        <span className="list-pagination__jump-suffix">
          / {softTotalPages}{hasMore ? '+' : ''}
        </span>
      )}
      <button type="submit" className="list-pagination__jump-btn" disabled={disabled}>
        移動
      </button>
    </form>
  ) : null;

  return (
    <div
      className={`list-pagination list-pagination--${variant}${isCursor ? ' list-pagination--cursor' : ''}${className ? ` ${className}` : ''}`}
      role="navigation"
      aria-label="ページネーション"
    >
      {!isCompact && (
        <div className="list-pagination__controls">
          <button
            type="button"
            className="list-pagination__nav list-pagination__nav--labeled"
            disabled={prevDisabled}
            onClick={() => goToPage(currentPage - 1)}
            aria-label="前のページ"
          >
            ‹ 前へ
          </button>

          {pageButtons}

          <button
            type="button"
            className="list-pagination__nav list-pagination__nav--labeled"
            disabled={nextDisabled}
            onClick={() => goToPage(currentPage + 1)}
            aria-label="次のページ"
          >
            次へ ›
          </button>
        </div>
      )}

      {isCompact && (
        <span className="list-pagination__range list-pagination__range--compact">{rangeLabel}</span>
      )}

      {isCompact && (
        <div className="list-pagination__controls">
          <button
            type="button"
            className="list-pagination__nav"
            disabled={prevDisabled}
            onClick={() => goToPage(currentPage - 1)}
            aria-label="前のページ"
          >
            ‹
          </button>

          {pageButtons}

          <button
            type="button"
            className="list-pagination__nav"
            disabled={nextDisabled}
            onClick={() => goToPage(currentPage + 1)}
            aria-label="次のページ"
          >
            ›
          </button>
        </div>
      )}

      {!isCompact && (
        <span className="list-pagination__range list-pagination__range--full">{rangeLabel}</span>
      )}

      {jumpControl}

      {onPageSizeChange && (
        <select
          className="list-pagination__size"
          value={pageSize}
          disabled={disabled}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          aria-label="表示件数"
        >
          {PAGE_SIZE_OPTIONS.map((n) => (
            <option key={n} value={n}>{n}件</option>
          ))}
        </select>
      )}
    </div>
  );
};

export default ListPagination;
